import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useAuthStore from '../../store/useAuthStore';
import toast from 'react-hot-toast';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { LockClosedIcon, EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline"; // Added Eye icons

const ResetPasswordPage = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const navigate = useNavigate();
    const { verifyResetToken, resetPassword } = useAuthStore();

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isTokenValid, setIsTokenValid] = useState(false);
    const [isLoading, setIsLoading] = useState(true); // Loading for token verification
    const [isResetting, setIsResetting] = useState(false); // Loading for password reset
    const [verificationError, setVerificationError] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    useEffect(() => {
        const checkToken = async () => {
            setIsLoading(true); // Start loading
            setVerificationError(''); // Clear previous errors
            setIsTokenValid(false); // Assume invalid until proven otherwise

            if (!token) {
                setVerificationError('Invalid or missing reset token.');
                setIsLoading(false);
                toast.error('Invalid or missing reset token.');
                // Optionally redirect after a delay or let user click Back to Login
                return;
            }

            try {
                const isValid = await verifyResetToken(token); // Call the store function
                setIsTokenValid(isValid);
                if (!isValid) {
                    setVerificationError('Invalid or expired reset token. Please request a new one.');
                    toast.error('Invalid or expired reset token.');
                     // Optionally redirect after a delay
                     // setTimeout(() => navigate('/forgot-password'), 3000);
                }
            } catch (error) {
                // This catch block might not be strictly necessary if verifyResetToken handles its own errors
                // and returns false, but it's good practice.
                setIsTokenValid(false);
                setVerificationError('Failed to verify token. Please try again or request a new link.');
                console.error("Token verification error:", error) // Log for debugging
            } finally {
                setIsLoading(false); // Stop loading regardless of outcome
            }
        };

        checkToken();
    }, [token, verifyResetToken]); // Rerun effect if token changes

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (newPassword !== confirmPassword) {
            toast.error('Passwords do not match.');
            return;
        }
        if (newPassword.length < 6) { // Example minimum length
             toast.error('Password must be at least 6 characters long.');
             return;
         }
         // Add more password complexity rules if needed (e.g., uppercase, number, symbol)

        setIsResetting(true);
        try {
            const success = await resetPassword(token, newPassword);
            if (success) {
                toast.success('Password reset successfully! You can now log in.');
                navigate('/login?type=builder'); // Redirect to login on success
            }
            // Error toast is handled in store if !success
        } catch (error) {
            // Catch errors thrown by the store (e.g., network issues)
            console.error("Password reset submission failed:", error);
             // Store function should already show a toast for API errors
        } finally {
            setIsResetting(false);
        }
    };

    return (
        <div
            className="min-h-screen flex items-center justify-center p-4"
            style={{ background: "linear-gradient(310deg, #555555 4%, white 38%)" }}
        >
            <div className="w-full max-w-md p-8 bg-white rounded-lg shadow-2xl border border-gray-300">
                <h2 className="text-2xl font-semibold text-center text-gray-800 mb-6">
                    Reset Password
                </h2>

                {/* --- Loading State --- */}
                {isLoading && (
                    <div className="flex justify-center items-center py-6">
                        <AiOutlineLoading3Quarters className="animate-spin text-3xl text-blue-500" />
                        <p className="ml-3 text-gray-600">Verifying link...</p>
                    </div>
                )}

                {/* --- Verification Error State --- */}
                {verificationError && !isLoading && (
                    <div className="mb-4 p-3 bg-red-100 text-red-700 text-sm rounded-md text-center">
                        <p>{verificationError}</p>
                         <button
                            onClick={() => navigate('/forgot-password')} // Navigate to forgot password page
                            className="mt-2 text-red-700 underline font-medium hover:text-red-800"
                        >
                            Request a new link?
                        </button>
                    </div>
                )}

                {/* --- Password Reset Form (Token Valid) --- */}
                {isTokenValid && !isLoading && !verificationError && (
                    <form onSubmit={handleSubmit}>
                        <div className="mb-4">
                            <label
                                htmlFor="newPassword"
                                className="block text-sm font-medium text-gray-700 mb-1"
                            >
                                New Password
                            </label>
                            <div className="relative">
                                <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                                    <LockClosedIcon className="w-5 h-5 text-black" />
                                </span>
                                <input
                                    type={showNewPassword ? "text" : "password"}
                                    id="newPassword"
                                    name="newPassword"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    className="w-full px-10 py-2 border rounded-lg bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                    style={{ backgroundColor: "#dddddd", borderRadius: "12px" }}
                                    placeholder="Enter new password (min 6 chars)"
                                    required
                                    minLength={6}
                                    disabled={isResetting}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                                    aria-label={showNewPassword ? "Hide password" : "Show password"}
                                >
                                    {showNewPassword ? <EyeSlashIcon className="h-5 w-5"/> : <EyeIcon className="h-5 w-5"/>}
                                </button>
                            </div>
                        </div>

                        <div className="mb-6">
                             <label
                                htmlFor="confirmPassword"
                                className="block text-sm font-medium text-gray-700 mb-1"
                            >
                                Confirm New Password
                            </label>
                            <div className="relative">
                                <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                                    <LockClosedIcon className="w-5 h-5 text-black" />
                                </span>
                                <input
                                    type={showConfirmPassword ? "text" : "password"}
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className="w-full px-10 py-2 border rounded-lg bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                    style={{ backgroundColor: "#dddddd", borderRadius: "12px" }}
                                    placeholder="Confirm new password"
                                    required
                                    minLength={6}
                                    disabled={isResetting}
                                />
                                 <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                                >
                                    {showConfirmPassword ? <EyeSlashIcon className="h-5 w-5"/> : <EyeIcon className="h-5 w-5"/>}
                                </button>
                             </div>
                        </div>

                        <div className="flex flex-col items-center">
                            <button
                                type="submit"
                                disabled={isResetting || !isTokenValid} // Double check validity
                                className={`w-full sm:w-auto px-6 py-2 mt-2 text-black border-2 transition-all duration-300 flex items-center justify-center ${
                                    (isResetting || !isTokenValid) ? "opacity-70 cursor-not-allowed" : "hover:bg-gray-50" // Add hover
                                }`}
                                style={{
                                    background: "linear-gradient(310deg, #dddddd 10%, white)",
                                    borderRadius: "24px",
                                }}
                            >
                                {isResetting ? (
                                    <>
                                        <AiOutlineLoading3Quarters className="animate-spin mr-2" />
                                        Resetting...
                                    </>
                                ) : (
                                    "Reset Password"
                                )}
                            </button>
                              {/* No need for Back to Login here, successful reset navigates away */}
                        </div>
                    </form>
                )}

                {/* --- Fallback: Show Back to Login if not loading and token invalid --- */}
                {!isLoading && !isTokenValid && (
                      <div className="flex justify-center mt-4">
                         <button
                            type="button"
                            onClick={() => navigate('/login?type=builder')}
                            className="text-sm text-blue-600 hover:underline"
                         >
                             Back to Login
                         </button>
                     </div>
                 )}
            </div>
        </div>
    );
};

export default ResetPasswordPage;